import React, { Context, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { usePrevious } from 'b2b-apps/shop/common/hooks';
import { getPathnameWithSearch } from 'b2b-apps/shop/routes/utils/url';
import {
  EnhancedComponentProps,
  EnhancedComponentType,
  ScrollPosition,
  WrappedComponentType,
} from './withScrollRestore.types';

const ScrollContext: Context<
  (scroll: string) => number
> = React.createContext<(scroll: string) => number>(() => 0);

const SCROLL_DEBOUNCE = 250;
const DONT_RESTORE_ON = ['brand', 'category', 'search'];


const withScrollRestore = <P extends EnhancedComponentProps>(
  Component: WrappedComponentType<P>,
): EnhancedComponentType<P> => {
  const WithScrollRestore = (props: P): ReactElement => {
    const [prevScrollPosition, setPrevScrollPosition] = useState<ScrollPosition>({});
    const prevPathName = usePrevious(props.location.pathname);

    useEffect(
      (): void => {
        //NOTE: it is only successful on pages without ajax data loading
        //To restore scroll on pages with dynamic data loading use ScrollContext
        if (props.history.action === 'POP') {
          window.scrollTo(
            0,
            prevScrollPosition[getPathnameWithSearch(props.location)] || 0,
          );
        } else {
          const currentPathname = props.location.pathname;
          const verifyIfRestore = (element: string) => currentPathname.includes(element);
          const shouldRestore: boolean = prevPathName !== currentPathname
            || !DONT_RESTORE_ON.some(verifyIfRestore);

          if (shouldRestore) {
            window.scrollTo(0, 0);
          }
        }
      },
      [props.location, props.history.action],
    );

    const handleScroll = useMemo(
      () => debounce(() => {
        setPrevScrollPosition({
          ...prevScrollPosition,
          [getPathnameWithSearch(props.location)]: window.pageYOffset,
        });
      }, SCROLL_DEBOUNCE),
      [props.location, setPrevScrollPosition, prevScrollPosition],
    );

    useEffect(
      () => {
        window.addEventListener('scroll', handleScroll);
        return () => {
          window.removeEventListener('scroll', handleScroll);
        };
      },
      [props.location],
    );

    const getScrollPosition = useCallback(
      (url: string): number => prevScrollPosition[url] || 0,
      [prevScrollPosition],
    );

    return (
      <ScrollContext.Provider value={getScrollPosition}>
        <Component {...props} />
      </ScrollContext.Provider>
    );
  };

  return withRouter(WithScrollRestore) as any;
};

export default withScrollRestore;
export {
  ScrollContext,
};
